import { useRef, forwardRef, useImperativeHandle } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser } from "lucide-react";
import { Label } from "@/components/ui/label";

const CANVAS_PROPS = { className: "w-full h-32 cursor-crosshair" };

const trimCanvas = (canvas) => {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return canvas;
  const pad = 4;
  const w = maxX - minX + 1 + pad * 2;
  const h = maxY - minY + 1 + pad * 2;
  const trimmed = document.createElement("canvas");
  trimmed.width = w;
  trimmed.height = h;
  trimmed.getContext("2d").drawImage(canvas, minX - pad, minY - pad, w, h, 0, 0, w, h);
  return trimmed;
};

export const SignaturePad = forwardRef(({ label, testId, clearTestId, hint }, ref) => {
  const sigRef = useRef(null);

  useImperativeHandle(ref, () => ({
    isEmpty: () => sigRef.current?.isEmpty() ?? true,
    toDataURL: () => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return null;
      return trimCanvas(canvas).toDataURL("image/png");
    },
    clear: () => sigRef.current?.clear(),
  }));

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-neutral-400 text-xs">{label}<span className="text-brand-red"> *</span></Label>
        <button
          type="button"
          data-testid={clearTestId}
          onClick={() => sigRef.current?.clear()}
          className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-white transition-colors"
        >
          <Eraser className="w-3 h-3" /> Borrar
        </button>
      </div>
      <div className="rounded-lg bg-white border border-white/10 overflow-hidden" data-testid={testId}>
        <SignatureCanvas
          ref={sigRef}
          penColor="black"
          backgroundColor="rgba(255,255,255,0)"
          canvasProps={CANVAS_PROPS}
        />
      </div>
      {hint && <p className="text-neutral-600 text-[11px]">{hint}</p>}
    </div>
  );
});
